/**
 * Utility functions for text formatting
 */

/**
 * Convert text to proper title case
 * Handles special cases for vehicle names and common abbreviations
 */
export function toTitleCase(str) {
  if (!str) return '';
  
  // Words that should stay lowercase unless they're the first word
  const smallWords = ['a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 
                      'in', 'into', 'of', 'on', 'or', 'the', 'to', 'with'];
  
  // Words that should stay uppercase
  const upperWords = ['TXT', 'RXV', 'EFI', 'AC', 'DC', 'HP', 'V8', 'V6', 'V4',
                      'AWD', '4WD', '2WD', 'RWD', 'FWD', 'SUV', 'XL', 'XLT', 
                      'LE', 'SE', 'GT', 'ST', 'RS', 'SS', 'RT', 'SRT', 'AMG',
                      'M3', 'M5', 'X3', 'X5', 'Q5', 'Q7', 'A4', 'A6', 'S4', 'S6',
                      'LT', 'LS', 'LTZ', 'SLT', 'SLE', 'SL', 'SR', 'SR5', 'TRD',
                      'EZGO', 'E-Z-GO'];

  // Special replacements for common variations
  const replacements = {
    'ezgo': 'EZGO',
    'e-z-go': 'E-Z-GO',
    'rxv': 'RXV',
    'txt': 'TXT',
    'efi': 'EFI',
    'drive2': 'Drive2',
    'drive 2': 'Drive2',
    'clubcar': 'Club Car',
    'club car': 'Club Car'
  };

  // First apply replacements for known patterns
  let result = str.toLowerCase();
  for (const [key, value] of Object.entries(replacements)) {
    const regex = new RegExp(`\\b${key}\\b`, 'gi');
    result = result.replace(regex, value);
  }

  // Then apply title case
  result = result.replace(/\w+/g, (word, index) => {
    const lowerWord = word.toLowerCase();
    
    // Check if it should stay uppercase
    if (upperWords.includes(word.toUpperCase())) {
      return word.toUpperCase();
    }
    
    // Check if it's a small word (but not the first word)
    if (index > 0 && smallWords.includes(lowerWord)) {
      return lowerWord;
    }
    
    // Otherwise, capitalize first letter
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });

  // Handle hyphenated words
  result = result.replace(/-(\w)/g, (match, letter) => '-' + letter.toUpperCase());

  // Handle measurements and specifications (e.g., "6" lift" -> '6" Lift')
  result = result.replace(/(\d+)\s*["""]\s*(\w+)/gi, (match, num, word) => {
    return `${num}" ${word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()}`;
  });

  // Handle lift/suspension specs
  result = result.replace(/(\d+)\s*(inch|in\.|in)\s+(lift|suspension)/gi, '$1" $3');

  return result;
}

/**
 * Format vehicle title from year, make, and model
 */
export function formatVehicleTitle(vehicle) {
  if (!vehicle) return '';
  
  const parts = [];
  
  if (vehicle.year) parts.push(vehicle.year);
  if (vehicle.make) parts.push(toTitleCase(vehicle.make));
  if (vehicle.model) parts.push(toTitleCase(vehicle.model));
  
  return parts.join(' ');
}

/**
 * Format vehicle title with additional details
 */
export function formatVehicleFullTitle(vehicle) {
  if (!vehicle) return '';
  
  let title = formatVehicleTitle(vehicle);
  
  // Add trim/details if available
  if (vehicle.trim && vehicle.trim !== vehicle.model) {
    title += ` ${toTitleCase(vehicle.trim)}`;
  }
  
  return title;
}
